import * as React from 'react';
import classname from 'classnames';
import { Maximize2, Redo2, Undo2 } from 'lucide-react';
import { App } from '../../../../pkg';
import BaseToolBar from '../BaseToolbar';

import './index.scss';

interface BottomToolbarProps {
  app: App;
}

interface AppHistoryAndZoomApi {
  can_undo?: () => boolean;
  can_redo?: () => boolean;
  handle_undo?: () => void;
  handle_redo?: () => void;
  fit_scene_to_viewport?: () => void;
  get_camera_scale?: () => number;
  reset_zoom_to_100?: () => void;
}

interface ToolbarState {
  canUndo: boolean;
  canRedo: boolean;
  zoomScale: number;
}

const DEFAULT_STATE: ToolbarState = {
  canUndo: false,
  canRedo: false,
  zoomScale: 1,
};

const BottomToolbar: React.FC<BottomToolbarProps> = ({ app }: BottomToolbarProps) => {
  const appApi = app as unknown as AppHistoryAndZoomApi;
  const [toolbarState, setToolbarState] = React.useState<ToolbarState>(DEFAULT_STATE);

  React.useEffect(() => {
    const syncToolbarState = () => {
      const nextCanUndo = appApi.can_undo?.() ?? false;
      const nextCanRedo = appApi.can_redo?.() ?? false;
      const nextZoom = appApi.get_camera_scale?.() ?? 1;

      setToolbarState(prev => {
        if (
          prev.canUndo === nextCanUndo &&
          prev.canRedo === nextCanRedo &&
          Math.abs(prev.zoomScale - nextZoom) < 0.0001
        ) {
          return prev;
        }

        return {
          canUndo: nextCanUndo,
          canRedo: nextCanRedo,
          zoomScale: nextZoom,
        };
      });
    };

    syncToolbarState();
    const timer = window.setInterval(syncToolbarState, 80);

    return () => {
      window.clearInterval(timer);
    };
  }, [appApi]);

  const zoomPercent = Math.round(toolbarState.zoomScale * 100);

  return (
    <BaseToolBar
      className="bottom-toolbar canvas-overlay"
      orientation="horizontal"
      ariaLabel="History and zoom toolbar"
      groups={[
        {
          key: 'history-zoom',
          className: 'tool-bar-group--inline',
          content: (
            <>
              <button
                type="button"
                className="tool-bar-button tool-bar-button--icon"
                aria-label="Undo"
                title="Undo"
                disabled={!toolbarState.canUndo}
                onClick={() => {
                  appApi.handle_undo?.();
                }}
              >
                <Undo2 />
              </button>
              <button
                type="button"
                className="tool-bar-button tool-bar-button--icon"
                aria-label="Redo"
                title="Redo"
                disabled={!toolbarState.canRedo}
                onClick={() => {
                  appApi.handle_redo?.();
                }}
              >
                <Redo2 />
              </button>
              <button
                type="button"
                className={classname('tool-bar-button', 'tool-bar-button--text', {
                  'is-default': zoomPercent === 100,
                })}
                aria-label="Reset zoom to 100%"
                title="Reset zoom to 100%"
                onClick={() => {
                  appApi.reset_zoom_to_100?.();
                }}
              >
                {zoomPercent}%
              </button>
              <button
                type="button"
                className="tool-bar-button tool-bar-button--icon"
                aria-label="Fit View"
                title="Fit View"
                onClick={() => {
                  appApi.fit_scene_to_viewport?.();
                }}
              >
                <Maximize2 />
              </button>
            </>
          ),
        },
      ]}
    />
  );
};

export default BottomToolbar;
